// AnnotateView shell — top bar, dual view, footer (progress + controls + submit).
// Spec §6.1, §6.3, §6.4, §6.5.
//
// PR8 adds the videos and interactivity (rVFC sync, scrub, jump, keyboard,
// zoom/pan). PR9-PR11 layer the overlay, keypoint interactions, and draft
// persistence on top.

import type * as React from 'react';
import type { SecPoseDraftTransferControlsProps } from '../../components/SecPoseDraftTransferControls';
import { useCarryForwardSeed } from '../../hooks/useCarryForwardSeed';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { DualView } from './DualView';
import { NextButton } from './NextButton';
import { PendingButton } from './PendingButton';
import { ProgressBar } from './ProgressBar';
import { SubmitButton } from './SubmitButton';
import { TopBar } from './TopBar';

export interface AnnotateViewProps {
  onSubmit: (toonText: string, frameCount: number, elapsedMs: number) => Promise<void>;
  onPending: () => void | Promise<void>;
  onQuit: () => void;
  submitting?: boolean;
  canSubmit?: boolean;
  submitBlockedReason?: string | undefined;
  draftTransfer?: SecPoseDraftTransferControlsProps | undefined;
}

export default function AnnotateView({
  onSubmit,
  onPending,
  onQuit,
  submitting = false,
  canSubmit = true,
  submitBlockedReason,
  draftTransfer,
}: AnnotateViewProps): React.ReactElement {
  useKeyboardShortcuts();
  useCarryForwardSeed();
  return (
    <div className="flex h-full flex-col bg-background text-foreground">
      <TopBar onQuit={onQuit} draftTransfer={draftTransfer} />
      <main className="min-h-0 flex-1 p-2">
        <DualView className="h-full" />
      </main>
      <footer className="flex shrink-0 items-stretch gap-4 border-t border-border bg-background px-4 py-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <NextButton onSubmit={onSubmit} submitting={submitting} />
          <ProgressBar />
        </div>
        <div className="flex w-28 flex-col gap-2">
          <PendingButton onPending={onPending} />
          <SubmitButton
            onSubmit={onSubmit}
            submitting={submitting}
            canSubmit={canSubmit}
            blockedReason={submitBlockedReason}
          />
        </div>
      </footer>
    </div>
  );
}
